import { useCallback, useMemo, useState } from 'react'
import {
	getAllNodeHistory,
	getStructureDetailV1,
	restoreNodeHistory,
} from 'src/endpoints'

import { getApiRef, useApiRequest } from '@/endpoints/hooks'
import { useAppDispatch, useAppStore } from '@/hooks'
import {
	loadNodeChildren,
	lockNode,
	unlockNode,
} from '@/store/modules/node/actions'
import { loadNode } from '@/store/modules/node/general-actions'
import { cancelEditingTab } from '@/store/modules/tab/actions'
import { isLockedByMe } from '@/utils/locking'
import { useStructureTypeActions } from '@/utils/structureType/useStructureTypeActions'

import { useDetailTabContext } from '../context/DetailTabContext'

/** Replace current version with a historical version via V2 restore. */
export const useReplaceVersionData = (
	changeVersion: (version: number) => void,
) => {
	const [replacing, setReplacing] = useState(false)
	const user = useAppStore((state) => state.auth.user)
	const request = useApiRequest()

	const {
		state: { node, editModeProp, version },
	} = useDetailTabContext()

	const dispatch = useAppDispatch()
	const { initData } = useStructureTypeActions(node.type)

	const afterReplace = useCallback(() => {
		changeVersion(1)
		setReplacing(false)
	}, [changeVersion])

	const replaceVersionData = useCallback(async () => {
		try {
			setReplacing(true)
			const isLockedbyMe = isLockedByMe(node, user)

			if (!node.lockTime) {
				await dispatch(lockNode(node))
			}

			if (version != null) {
				await request(restoreNodeHistory(node.id, version))
			}

			await dispatch(loadNodeChildren(node.id))
			await dispatch(loadNode(node.id))
			await dispatch(
				initData({
					nodeId: node.id,
					editMode: editModeProp,
					force: true,
				}),
			)
			getApiRef(getAllNodeHistory(node.id)).invalidate()
			getApiRef(getStructureDetailV1(node.id)).invalidate()

			if (!editModeProp) {
				dispatch(cancelEditingTab(node))

				if (!isLockedbyMe) {
					await dispatch(unlockNode(node))
				}
			}

			afterReplace()
		} catch (e) {
			setReplacing(false)
			throw e
		}
	}, [
		node,
		user,
		version,
		dispatch,
		request,
		initData,
		editModeProp,
		afterReplace,
	])

	const info = useMemo(
		() => ({ replacing, replaceVersionData }),
		[replaceVersionData, replacing],
	)

	return info
}
